import { useEffect, useState } from "react";

import { fetchHomeSection, HomeSection } from "@/services/homeService";

import appHomeImage from "../assets/images/hareenghome.png";

export default function AboutHareengSection() {
  const [section, setSection] = useState<HomeSection<any> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomeSection("about").then((data) => {
      setSection(data);
      setLoading(false);
    });
  }, []);

  // Modern shimmer for loading
  function ModernShimmer({
    className = "",
    height,
    width,
  }: {
    className?: string;
    height: string;
    width: string;
  }) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
        style={{ height, width }}
      >
        <div
          className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
          style={{ backgroundSize: "200% 100%" }}
        />
      </div>
    );
  }

  if (
    typeof document !== "undefined" &&
    !document.getElementById("modern-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "modern-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 1.5s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <section className="w-full bg-base-200/50 py-8 md:py-16">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-4 md:flex-row md:gap-10">
        {/* Text Content */}
        <div className="w-full md:flex-1">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body p-6 md:p-8">
              {loading ? (
                <>
                  <ModernShimmer
                    className="mx-auto mb-4"
                    height="3rem"
                    width="70%"
                  />
                  <ModernShimmer
                    className="mx-auto mb-6"
                    height="2rem"
                    width="90%"
                  />
                  <ModernShimmer
                    className="mx-auto mb-3"
                    height="1.5rem"
                    width="80%"
                  />
                  <ModernShimmer
                    className="mx-auto mb-3"
                    height="1.5rem"
                    width="60%"
                  />
                  <ModernShimmer
                    className="mx-auto mb-3"
                    height="1.5rem"
                    width="70%"
                  />
                  <ModernShimmer
                    className="mx-auto mb-3"
                    height="1.5rem"
                    width="50%"
                  />
                </>
              ) : section && section.content ? (
                <>
                  <h2 className="card-title mb-4 text-2xl font-extrabold text-primary md:text-4xl lg:text-5xl">
                    {section.content.title}
                  </h2>
                  <p className="mb-6 text-base leading-relaxed text-base-content/70 md:text-lg">
                    {section.content.description}
                  </p>
                  <ul className="space-y-3 text-sm text-base-content/80 md:text-base">
                    {section.content.bullets?.map((b: string, i: number) => (
                      <li className="flex items-start gap-2" key={i}>
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Image Section */}
        <div className="w-full md:flex-1">
          <div className="relative overflow-hidden rounded-2xl shadow-xl">
            {loading ? (
              <ModernShimmer className="mx-auto" height="400px" width="100%" />
            ) : (
              <img
                alt={section?.content?.title || "Hareeng app home screen"}
                className="h-64 w-full object-cover sm:h-80 md:h-96 lg:h-[500px]"
                loading="lazy"
                src={
                  section?.content?.image
                    ? section.content.image.startsWith("http")
                      ? section.content.image
                      : `${import.meta.env.VITE_API_STORAGE_URL}${section.content.image}`
                    : appHomeImage
                }
              />
            )}
            {/* Overlay for better text readability if needed */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
